import React from 'react';
import { Box, CircularProgress, Tooltip, Typography } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RTooltip,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts';
import { useFetchAdGroupMetricsQuery } from '../../slices/bidRecommendationSlice';
import {
  BidChangeEvent,
  DailyMetricPoint,
} from '../../types/bidRecommendations';

interface MetricsChartsProps {
  adGroupId: number;
  platform: string;
}

interface ChartConfig {
  key: keyof DailyMetricPoint;
  label: string;
  color: string;
  format: (v: number) => string;
}

const CHARTS: ChartConfig[] = [
  {
    key: 'cpa',
    label: 'CPA',
    color: '#1976d2',
    format: (v) => `$${v.toFixed(2)}`,
  },
  { key: 'roas', label: 'ROAS', color: '#2e7d32', format: (v) => v.toFixed(2) },
  {
    key: 'dih_rate',
    label: 'DIH Rate',
    color: '#ed6c02',
    format: (v) => `${v.toFixed(1)}%`,
  },
  {
    key: 'cost_per_lead',
    label: 'Cost / Lead',
    color: '#9c27b0',
    format: (v) => `$${v.toFixed(2)}`,
  },
  {
    key: 'top_word_pct',
    label: 'Top Word %',
    color: '#0288d1',
    format: (v) => `${v.toFixed(1)}%`,
  },
  {
    key: 'human_rate',
    label: 'Human Rate',
    color: '#d32f2f',
    format: (v) => `${v.toFixed(1)}%`,
  },
  {
    key: 'cpa_trend',
    label: 'CPA Trend (7d Avg)',
    color: '#455a64',
    format: (v) => `$${v.toFixed(2)}`,
  },
];

function formatDateLabel(dateStr: string) {
  const d = new Date(dateStr + 'T00:00:00');
  return `${d.getMonth() + 1}/${d.getDate()}`;
}

function formatBidChangeLabel(bc: BidChangeEvent): string {
  if (bc.old_bid != null) {
    return `$${bc.old_bid.toFixed(2)} → $${bc.bid_value.toFixed(2)}`;
  }
  return `$${bc.bid_value.toFixed(2)}`;
}

function formatBidChangeTooltip(bc: BidChangeEvent): string {
  const parts: string[] = [];
  if (bc.old_bid != null) {
    parts.push(`$${bc.old_bid.toFixed(2)} → $${bc.bid_value.toFixed(2)}`);
  } else {
    parts.push(`New bid: $${bc.bid_value.toFixed(2)}`);
  }
  const sourceLabel = bc.source === 'google_ads' ? 'Google Ads' : 'AdVantage';
  parts.push(`via ${sourceLabel}`);
  if (bc.user_email) {
    parts.push(bc.user_email);
  }
  return parts.join(' · ');
}

const MetricsCharts: React.FC<MetricsChartsProps> = ({
  adGroupId,
  platform,
}) => {
  const { data, isLoading, error } = useFetchAdGroupMetricsQuery({
    adGroupId,
    platform,
    days: 30,
  });

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  if (error || !data) {
    return (
      <Typography variant="body2" color="text.secondary" py={2}>
        Unable to load metrics.
      </Typography>
    );
  }

  if (data.metrics.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary" py={2}>
        No metric data available for this ad group.
      </Typography>
    );
  }

  const chartData = data.metrics.map((m) => ({
    ...m,
    dateLabel: formatDateLabel(m.date),
  }));

  return (
    <Box>
      <Typography variant="subtitle2" gutterBottom>
        30-Day Performance
      </Typography>
      {CHARTS.map((cfg) => {
        const hasData = chartData.some((d) => d[cfg.key] != null);
        if (!hasData) return null;
        return (
          <Box key={cfg.key} mb={2}>
            <Typography
              variant="caption"
              color="text.secondary"
              mb={0.5}
              display="block"
            >
              {cfg.label}
            </Typography>
            <ResponsiveContainer width="100%" height={140}>
              <LineChart
                data={chartData}
                margin={{ top: 22, right: 8, left: 0, bottom: 0 }}
              >
                <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                <XAxis
                  dataKey="dateLabel"
                  tick={{ fontSize: 10 }}
                  interval="preserveStartEnd"
                />
                <YAxis
                  tick={{ fontSize: 10 }}
                  width={50}
                  tickFormatter={(v: number) => cfg.format(v)}
                />
                <RTooltip
                  formatter={(value: any) => [
                    cfg.format(Number(value)),
                    cfg.label,
                  ]}
                  labelFormatter={(_label: any, payload: any) => {
                    const pt = payload?.[0]?.payload;
                    return pt?.date || String(_label);
                  }}
                />
                <Line
                  type="monotone"
                  dataKey={cfg.key}
                  stroke={cfg.color}
                  strokeWidth={2}
                  dot={false}
                  connectNulls
                />
                {data.bid_changes.map((bc, i) => {
                  const isExternal = bc.source === 'google_ads';
                  const positions = [
                    'insideTopRight',
                    'insideBottomRight',
                    'insideTopLeft',
                    'insideBottomLeft',
                  ] as const;
                  return (
                    <ReferenceLine
                      key={`bid-${i}`}
                      x={formatDateLabel(bc.date)}
                      stroke={isExternal ? '#e65100' : '#888'}
                      strokeDasharray="4 3"
                      strokeWidth={1.5}
                      label={{
                        value: formatBidChangeLabel(bc),
                        position: positions[i % positions.length],
                        fontSize: 9,
                        fill: isExternal ? '#e65100' : '#666',
                        offset: 4,
                      }}
                    />
                  );
                })}
              </LineChart>
            </ResponsiveContainer>
          </Box>
        );
      })}

      {data.bid_changes.length > 0 && (
        <Box mt={1} mb={2}>
          <Typography variant="caption" color="text.secondary" display="block">
            Dashed lines indicate bid changes.
          </Typography>
          {data.bid_changes.map((bc, i) => (
            <Box key={i} display="flex" alignItems="center" gap={0.5} mt={0.5}>
              <Box
                sx={{
                  width: 16,
                  height: 0,
                  borderTop: `2px dashed ${bc.source === 'google_ads' ? '#e65100' : '#888'}`,
                }}
              />
              <Tooltip
                title={formatBidChangeTooltip(bc)}
                arrow
                placement="right"
              >
                <Box
                  display="flex"
                  alignItems="center"
                  gap={0.5}
                  sx={{ cursor: 'pointer' }}
                >
                  <Typography variant="caption" color="text.secondary">
                    {bc.date}: {formatBidChangeLabel(bc)}{' '}
                    <Typography
                      component="span"
                      variant="caption"
                      sx={{
                        color:
                          bc.source === 'google_ads'
                            ? '#e65100'
                            : 'text.secondary',
                      }}
                    >
                      ({bc.source === 'google_ads' ? 'Google Ads' : 'AdVantage'}
                      )
                    </Typography>
                  </Typography>
                  <InfoOutlinedIcon
                    sx={{ fontSize: 12, color: 'text.disabled' }}
                  />
                </Box>
              </Tooltip>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default MetricsCharts;
